import type { User } from '@supabase/supabase-js'
import {
  getDisplayNameFromUserMetadata,
  getInitialProfileRoleFromMetadata,
  getPhoneFromUserMetadata,
} from '../lib/appProfileFromUserMetadata'
import { normalizeGeboorteIso } from '../lib/klantInfoFromProfile'
import { supabase } from './supabase'

/** Rol: superadmin = platform; admin/salon = salonbeheerder; salon_staff = team zonder volledige admin; user = klant */
export type AppRole = 'superadmin' | 'admin' | 'salon' | 'salon_staff' | 'user'

/** Rol in `salon_members` (per salon). */
export type SalonMemberRole = 'admin' | 'manager' | 'employee'

/** Opgeslagen in `app_profiles.gender` (kolom `gender`). */
export type ProfileGender = 'man' | 'vrouw' | 'anders' | 'liever_niet'

export interface AppProfile {
  role: AppRole
  displayName: string | null
  phone: string | null
  /** ISO-datum `YYYY-MM-DD` */
  birthDate: string | null
  gender: ProfileGender | null
  /** Salon-IDs met rol `admin` (volledig beheer; uitnodigen, instellingen, betaling). */
  salonIds: string[]
  /** Alle salons waar de gebruiker teamlid is (admin, manager of employee). */
  staffSalonIds: string[]
  /** `salon_id` → rol in die salon. */
  salonMemberRoles: Partial<Record<string, SalonMemberRole>>
}

export async function getProfile(userId: string): Promise<AppProfile | null> {
  const [profileRes, membersRes] = await Promise.all([
    supabase
      .from('app_profiles')
      .select('role, display_name, phone, birth_date, gender')
      .eq('user_id', userId)
      .maybeSingle(),
    supabase.from('salon_members').select('salon_id, role').eq('user_id', userId),
  ])

  if (profileRes.error) throw profileRes.error
  if (membersRes.error) throw membersRes.error

  const ap = profileRes.data
  const members = membersRes.data ?? []

  const adminSalonIds = members.filter((m) => m.role === 'admin').map((m) => m.salon_id)
  const staffSalonIds = [...new Set(members.map((m) => m.salon_id))]

  const salonMemberRoles: Partial<Record<string, SalonMemberRole>> = {}
  for (const m of members) {
    salonMemberRoles[m.salon_id] = m.role as SalonMemberRole
  }

  const role: AppRole =
    ap?.role === 'superadmin'
      ? 'superadmin'
      : ap?.role === 'salon'
        ? 'salon'
        : adminSalonIds.length > 0
          ? 'admin'
          : staffSalonIds.length > 0
            ? 'salon_staff'
            : 'user'

  const genderRaw = ap?.gender as string | null | undefined
  const gender: ProfileGender | null =
    genderRaw === 'man' ||
    genderRaw === 'vrouw' ||
    genderRaw === 'anders' ||
    genderRaw === 'liever_niet'
      ? genderRaw
      : null

  return {
    role,
    displayName: ap?.display_name ?? null,
    phone: ap?.phone?.trim() ? ap.phone.trim() : null,
    birthDate: normalizeGeboorteIso(ap?.birth_date as string | null) ?? null,
    gender,
    salonIds: adminSalonIds,
    staffSalonIds,
    salonMemberRoles,
  }
}

/** Profielvelden die de client mag wijzigen (role wordt server-side beheerd). */
export type AppProfileUpsertData = {
  displayName?: string | null
  phone?: string | null
  birthDate?: string | null
  gender?: ProfileGender | null
}

export async function upsertAppProfile(
  userId: string,
  data: AppProfileUpsertData
): Promise<void> {
  const payload: Record<string, unknown> = { updated_at: new Date().toISOString() }
  if (data.displayName !== undefined) payload.display_name = data.displayName
  if (data.phone !== undefined) payload.phone = data.phone
  if (data.birthDate !== undefined) payload.birth_date = data.birthDate
  if (data.gender !== undefined) payload.gender = data.gender

  const { data: existing, error: selectError } = await supabase
    .from('app_profiles')
    .select('user_id')
    .eq('user_id', userId)
    .maybeSingle()

  if (selectError) throw selectError

  if (existing) {
    const { error } = await supabase.from('app_profiles').update(payload).eq('user_id', userId)
    if (error) throw error
    return
  }

  const insertRow: Record<string, unknown> = {
    user_id: userId,
    role: 'user',
    ...payload,
  }
  const { error } = await supabase.from('app_profiles').insert(insertRow)
  if (error) throw error
}

type AppProfileSeed = {
  displayName?: string | null
  phone?: string | null
}

/** Eerste profiel bij registratie; role alleen user of salon (salon_owner-flow). */
export async function createInitialAppProfile(
  userId: string,
  role: 'user' | 'salon',
  seed?: AppProfileSeed
): Promise<void> {
  const row: Record<string, unknown> = {
    user_id: userId,
    role,
    updated_at: new Date().toISOString(),
  }
  if (seed?.displayName) row.display_name = seed.displayName
  if (seed?.phone) row.phone = seed.phone
  const { error } = await supabase.from('app_profiles').insert(row)
  if (error) throw error
}

/**
 * Zorg dat ingelogde gebruiker een app_profiles-rij heeft (o.a. na e-mailbevestiging).
 * Vult lege display_name/phone vanuit user_metadata; overschrijft bestaande waarden niet.
 */
export async function ensureAppProfileExists(user: User): Promise<void> {
  const userId = user.id
  const meta = (user.user_metadata ?? {}) as Record<string, unknown>
  const role = getInitialProfileRoleFromMetadata(meta)
  const displayName = getDisplayNameFromUserMetadata(user)
  const phone = getPhoneFromUserMetadata(meta)

  const { data: existing, error: selectError } = await supabase
    .from('app_profiles')
    .select('user_id, display_name, phone')
    .eq('user_id', userId)
    .maybeSingle()

  if (selectError) throw selectError

  if (!existing) {
    await createInitialAppProfile(userId, role, {
      displayName: displayName ?? undefined,
      phone: phone ?? undefined,
    })
    return
  }

  const patch: Record<string, unknown> = { updated_at: new Date().toISOString() }
  if (!existing.display_name?.trim() && displayName) patch.display_name = displayName
  if (!existing.phone?.trim() && phone) patch.phone = phone
  if (Object.keys(patch).length <= 1) return

  const { error: updateError } = await supabase
    .from('app_profiles')
    .update(patch)
    .eq('user_id', userId)
  if (updateError) throw updateError
}

/** Promoveer salon_owner naar role salon (RPC; idempotent). */
export async function promoteAppProfileSalonOwner(): Promise<void> {
  const { error } = await supabase.rpc('promote_app_profile_salon_owner')
  if (error) throw error
}
